import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import PageHeader from "../../core/common/commonComponents/PageHeader";
import { exportToCSV, exportToExcel } from "../../core/common/GlobalFunctions/exportTableData";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import ProvidentFundModal from "./modals/provident_fund_Modal";
import DeleteModal1 from "../../core/modals/deleteModal1";
import { Button, Drawer, InputNumber, message, Select } from "antd";
import { createProvidentFund, deleteProvidentFund, fetchAllProvidentFunds } from "../../core/data/redux/payroll/Accounting/providentFund/providentFundActions";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import { fetchEmployeeSalaryStructures } from "../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { fetchAllPfEmployees } from "../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import { getEmployees } from "../../core/data/redux/employee/employeeAction";
import { calculatePfAmounts } from "./utils/pfCalculationUtils"; // keep the same path you used in the modal
import { fetchEarnings } from "../../core/data/redux/payroll/payrollComponent/earning/earningActions";
import PFStatusFilterHeader from "../../core/common/commonComponents/Pftableheader";
import { filterByStatus } from "../../core/utils/filterActiveData";

const ProvidentFund = () => {

    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { records } = useSelector((state: any) => state.providentFund.employeeContribution);
    const { user } = useSelector((state: any) => state.auth);
    const { earnings = [] } = useSelector((state: any) => state.payroll.earning);

    const now = new Date();
    const [filterYear, setFilterYear] = useState<number | "All">(now.getFullYear());
    const [filterMonth, setFilterMonth] = useState<number | "All">(now.getMonth() + 1);


    const { salaryStructures = [] } = useSelector((state: any) => state.payroll.salaryStructure);
    const { records: pfemployeeMapping = [] } = useSelector((state: any) => state.providentFund.pfEmployeeMapping);
    const employeeMapping = filterByStatus(pfemployeeMapping, 'active', 'status');
    const { employees = [] } = useSelector((state: any) => state.employee);

    const [autoLoading, setAutoLoading] = useState(false);
    const [autoOpen, setAutoOpen] = useState(false);
    const [autoYear, setAutoYear] = useState<number>(now.getFullYear());
    const [autoMonth, setAutoMonth] = useState<number>(now.getMonth() + 1);
    const [autoPfType, setAutoPfType] = useState<"mandatory" | "voluntary">("mandatory");
    const [autoEmpPct, setAutoEmpPct] = useState<number>(12);
    const [autoOrgPct, setAutoOrgPct] = useState<number>(12);
    const [autoHeadIds, setAutoHeadIds] = useState<number[]>([]);


    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };

    useEffect(() => {
        dispatch(getEmployees());                           // <-- as requested
        dispatch(fetchEmployeeSalaryStructures());
        dispatch(fetchAllPfEmployees());
        dispatch(fetchAllProvidentFunds());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchEarnings());
    }, [dispatch])

    const filtereddata = useMemo(() => {
        let data = [...records];

        if (selectedStatus && selectedStatus !== "All") {
            data = data.filter((r: any) => r.status === selectedStatus);
        }

        if (filterYear !== "All") {
            data = data.filter((r: any) => Number(r.year) === Number(filterYear));
        }

        if (filterMonth !== "All") {
            data = data.filter((r: any) => Number(r.month) === Number(filterMonth));
        }

        return filterData(data, {}, "createdAt", "createdAt", selectedSort);
    }, [records, selectedStatus, selectedSort, filterYear, filterMonth]);

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, filtereddata]);

    const handleDeleteProvidentFund = async (id: number) => {
        try {
            const result = await dispatch(deleteProvidentFund(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Provident fund deleted successfully');
                setTableKey((prevKey) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.error((result.payload as { error: string })?.error);
            }
        } catch (error) {
            message.error('Something went wrong');
        }
    };

    const handleProcessAutoPF = async () => {
        if (!autoYear) {
            message.error("Please select Year.");
            return;
        }
        if (!autoMonth || autoMonth < 1 || autoMonth > 12) {
            message.error("Please select a valid Month.");
            return;
        }
        if (autoEmpPct < 0 || autoOrgPct < 0) {
            message.error("Percentages must be 0 or higher.");
            return;
        }
        if (!autoHeadIds.length) {
            message.error("Please choose at least one earning head.");
            return;
        }

        try {
            setAutoLoading(true);

            // Eligible: mapped + employee active + PF enabled + has salary structure with earnings
            const mapped = Array.isArray(employeeMapping) ? employeeMapping : [];
            const eligibleMaps = mapped.filter((map: any) => {
                const empId = Number(map.EmployeeID ?? map.employee_id ?? map.employeeId);
                const emp = employees.find((e: any) => Number(e.EmployeeID) === empId);
                if (!emp) return false;

                const pfEnabled = Number(emp.pfEnable) === 1;
                const isActive = String(emp.employee_status || "").toLowerCase() === "active";

                const ss = salaryStructures.find(
                    (s: any) => Number(s.employee_id) === empId && Array.isArray(s.earnings) && s.earnings.length > 0
                );
                return pfEnabled && isActive && !!ss;
            });

            if (!eligibleMaps.length) {
                message.warning("No eligible employees found for auto PF contribution.");
                return;
            }

            let created = 0;
            let skippedNoStruct = 0; // no SS or no earnings
            let skippedNoMatch = 0;  // SS exists but none of the selected heads match
            let failed = 0;

            const errorMessages: { [key: string]: number } = {
                "For the selected month, contribution already exists.": 0,
            };

            const headSet = new Set(autoHeadIds);

            const results = await Promise.allSettled(
                eligibleMaps.map(async (map: any) => {
                    const empId = Number(map.EmployeeID ?? map.employee_id ?? map.employeeId);
                    const ss = salaryStructures.find(
                        (s: any) => Number(s.employee_id) === empId && Array.isArray(s.earnings) && s.earnings.length > 0
                    );
                    if (!ss) {
                        skippedNoStruct++;
                        return;
                    }

                    // Keep only rows whose earning_id is in selected heads
                    const rows = (ss.earnings || []).filter((r: any) =>
                        headSet.has(Number(r.earning_id ?? r.EarningID ?? r.earningId))
                    );

                    if (!rows.length) {
                        skippedNoMatch++;
                        return;
                    }

                    const selectedEarningIds = rows.map((r: any) => Number(r.id));
                    const selectedEarnings = rows.map((r: any) => ({
                        id: Number(r.id),
                        amount: Number(r.amount || 0),
                    }));

                    const { employeeShareAmount, organizationShareAmount } = calculatePfAmounts({
                        providentFundType: autoPfType,
                        employeeSharePercent: autoEmpPct,
                        organizationSharePercent: autoOrgPct,
                        selectedEarnings,
                    });

                    const pfEmployeeId = String(map.pfEmployeeId ?? map.pf_employee_id ?? map.PFEmployeeID ?? "");

                    const payload = {
                        pfEmployeeId,
                        provident_fund_type: autoPfType,
                        employee_share_percent: autoEmpPct,
                        organization_share_percent: autoOrgPct,
                        employee_share_amount: employeeShareAmount,
                        organization_share_amount: organizationShareAmount,
                        selected_earning_ids: selectedEarningIds,
                        description: `Auto PF for ${autoMonth}-${autoYear}`,
                        status: "active",
                        createdBy: user?.UserId || 1,
                        year: autoYear,
                        month: autoMonth,
                    };

                    try {
                        await dispatch(createProvidentFund(payload)).unwrap();
                        created++;
                    } catch (error: any) {
                        if (error?.status === "error" && error?.error === "For the selected month, contribution already exists.") {
                            errorMessages["For the selected month, contribution already exists."]++;
                        } else {
                            failed++;
                        }
                    }
                })
            );

            failed += results.filter((r) => r.status === "rejected").length;

            if (created) message.success(`Auto PF: ${created} contribution(s) created`);
            if (skippedNoStruct) message.warning(`${skippedNoStruct} skipped (no salary structure/earnings)`);
            if (skippedNoMatch) message.warning(`${skippedNoMatch} skipped (no matching earning heads)`);
            if (failed) message.error(`${failed} failed`);

            if (errorMessages["For the selected month, contribution already exists."] > 0) {
                message.error(
                    `${errorMessages["For the selected month, contribution already exists."]} contribution(s) failed: For the selected month, contribution already exists.`
                );
            }

            await dispatch(fetchAllProvidentFunds());
            setAutoOpen(false);
            setTableKey((k) => k + 1);
        } catch {
            message.error("Auto contribution failed.");
        } finally {
            setAutoLoading(false);
        }
    };


    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <Link
                        to="#"
                        className="avatar avatar-md"
                    >
                        <img src={record.employee_picture_url ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"} alt="img" className='img-fluid rounded-circle' />
                    </Link>
                    <div className="ms-2">
                        <p className="text-dark mb-0">
                            <Link
                                to="#"
                            >{record.employee_name}
                            </Link>
                        </p>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.Name.length - b.Name.length,
        },
        {
            title: "Contribution Type",
            dataIndex: "provident_fund_type",
            sorter: (a: any, b: any) =>
                a.provident_fund_type.length - b.provident_fund_type.length,
        },
        {
            title: "Employee Share",
            dataIndex: "employee_share_amount",
            sorter: (a: any, b: any) => parseFloat(a.employee_share_amount) - parseFloat(b.employee_share_amount),
            render: (_: any, record: any) => (
                <span>
                    {record.employee_share_amount}{" "}
                    <span style={{ color: '#6c757d' }}>
                        ({parseFloat(record.employee_share_percent)}%)
                    </span>
                </span>
            ),
        },
        {
            title: "Organization Share",
            dataIndex: "organization_share_amount",
            render: (_: any, record: any) => (
                <span>
                    {record.organization_share_amount}{" "}
                    <span style={{ color: '#6c757d' }}>
                        ({parseFloat(record.organization_share_percent)}%)
                    </span>
                </span>
            ),
            sorter: (a: any, b: any) => parseFloat(a.organization_share_amount) - parseFloat(b.organization_share_amount),
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: string, record: any) => {
                const isSalaryProcessed = record.salary_status === "Processed";

                return (
                    <div className="action-icon d-inline-flex">
                        <button
                            className="me-2"
                            onClick={() => {
                                if (!isSalaryProcessed) {
                                    showModal("edit");
                                    setSelectedRowData(record);
                                }
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={
                                isSalaryProcessed
                                    ? "Salary Processed - Edit Disabled"
                                    : "Edit"
                            }
                            disabled={isSalaryProcessed}
                            style={{
                                cursor: isSalaryProcessed ? "not-allowed" : "pointer",
                                opacity: isSalaryProcessed ? 0.5 : 1,
                            }}
                        >
                            <i className="ti ti-edit" />
                        </button>

                        <button
                            onClick={() => {
                                if (!isSalaryProcessed) {
                                    setSelectedRowData(record);
                                    setIsDeleteModalVisible(true);
                                }
                            }}
                            className="me-2"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={
                                isSalaryProcessed
                                    ? "Salary Processed - Delete Disabled"
                                    : "Delete"
                            }
                            disabled={isSalaryProcessed}
                            style={{
                                cursor: isSalaryProcessed ? "not-allowed" : "pointer",
                                opacity: isSalaryProcessed ? 0.5 : 1,
                            }}
                        >
                            <i className="ti ti-trash" />
                        </button>
                    </div>
                );
            },
        }


    ];
    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <PageHeader
                        title="Contribution"
                        breadcrumbs={[
                            { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
                            { label: "Accounting" },
                            { label: "Contribution List", isActive: true },
                        ]}
                        // listViewPath={all_routes.clientlist}
                        listViewPath="#"
                        onExportCSV={() => exportToCSV(filtereddata, 'ProvidentFundData')}
                        onExportExcel={() => exportToExcel(filtereddata, 'ProvidentFundData')}
                        onAdd={() => showModal('create')}
                        addLabel="Add Contribution"
                        exportEnabled
                        addButtonEnabled
                        rightExtra={
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    type="default"
                                    className="d-inline-flex align-items-center "
                                    onClick={() => setAutoOpen(true)}
                                    disabled={autoLoading}
                                    title="Auto calculate & create PF for mapped employees"
                                    style={{ paddingTop: '20px', paddingBottom: '20px' }}
                                >
                                    {autoLoading && <span className="spinner-border spinner-border-sm me-2" />}
                                    <i className="ti ti-robot me-1" />
                                    Auto Contribution
                                </Button>
                                <CollapseHeader />
                            </div>
                        }
                    />
                    {/* /Breadcrumb */}
                    <div className="card">
                        <PFStatusFilterHeader
                            title="Contribution List"
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                            filterYear={filterYear}
                            setFilterYear={setFilterYear}
                            filterMonth={filterMonth}
                            setFilterMonth={setFilterMonth}
                        />

                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filtereddata} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            <ProvidentFundModal modalType={modalType} handleCancel={handleCancel} isModalVisible={isModalVisible} selectedRowData={selectedRowData} />
            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.provident_fund_id && handleDeleteProvidentFund(selectedRowData.provident_fund_id)}
            />
            <Drawer
                title="Auto PF Process"
                placement="right"
                width={520}
                onClose={() => setAutoOpen(false)}
                open={autoOpen}
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Year <span className="text-danger">*</span></label>
                        <Select
                            value={autoYear}
                            onChange={(v) => setAutoYear(v)}
                            className="w-100"
                        >
                            {Array.from({ length: 21 }, (_, i) => {
                                const year = new Date().getFullYear() - 10 + i;
                                return (
                                    <Select.Option key={year} value={year}>
                                        {year}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Month <span className="text-danger">*</span></label>
                        <Select
                            value={autoMonth}
                            onChange={(v) => setAutoMonth(v)}
                            className="w-100"
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="col-md-12 mb-3">
                        <label className="form-label">Provident Fund Type <span className="text-danger">*</span></label>
                        <Select
                            value={autoPfType}
                            onChange={(v) => setAutoPfType(v)}
                            className="w-100"
                        >
                            <Select.Option value="mandatory">Mandatory Provident Fund</Select.Option>
                            <Select.Option value="voluntary">Voluntary Provident Fund</Select.Option>
                        </Select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Employee Share (%) <span className="text-danger">*</span></label>
                        <InputNumber
                            min={0}
                            value={autoEmpPct}
                            onChange={(v) => setAutoEmpPct(Number(v || 0))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Organization Share (%) <span className="text-danger">*</span></label>
                        <InputNumber
                            min={0}
                            value={autoOrgPct}
                            onChange={(v) => setAutoOrgPct(Number(v || 0))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div className="col-md-12 mb-3">
                        <label className="form-label">
                            Earning Heads to include <span className="text-danger">*</span>
                        </label>
                        <Select
                            mode="multiple"
                            className="w-100 custom-select"
                            placeholder="Pick earning heads (e.g., Basic, HRA, TA)"
                            value={autoHeadIds}
                            onChange={(vals) => setAutoHeadIds(vals as number[])}
                            options={(earnings || []).map((h: any) => ({
                                value: Number(h.Id),
                                label: h.Name,
                            }))}
                        />
                        <small className="text-muted">
                            For each employee, only salary-structure rows matching these heads will be summed into Gross.
                        </small>
                    </div>
                </div>

                <div className="d-flex justify-content-end">
                    <button className="btn btn-light me-2" onClick={() => setAutoOpen(false)} disabled={autoLoading}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleProcessAutoPF}
                        disabled={autoLoading}
                    >
                        {autoLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        Process PF
                    </button>
                </div>
            </Drawer>
        </>
    );
};
export default ProvidentFund;
